import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { isOnTrial, getTrialDaysRemaining, getTrialHoursRemaining } from '../../utils/trialHelpers';

/**
 * TrialBanner - Small, calm, persistent countdown banner for trial users
 */
const TrialBanner = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);

  // Check if user is on trial
  const onTrial = isOnTrial(user);
  const daysRemaining = getTrialDaysRemaining(user);
  const hoursRemaining = getTrialHoursRemaining(user);

  // Debug: Log trial info (can remove later)
  useEffect(() => {
    if (user && user.trial_end_date) {
      console.log('🔍 Trial Debug:', {
        trial_end_date: user.trial_end_date,
        daysRemaining,
        hoursRemaining,
        onTrial
      });
    }
  }, [user, daysRemaining]);

  // Update countdown every minute
  useEffect(() => {
    if (!onTrial) return;

    const interval = setInterval(() => {
      // Force re-render to update countdown
      setVisible(true);
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [onTrial]);

  // Don't show if not on trial
  if (!onTrial || !visible) return null;

  // Format time remaining message
  const getTimeMessage = () => {
    if (daysRemaining >= 2) {
      return `${daysRemaining} days left in your trial`;
    } else if (daysRemaining === 1) {
      return `1 day left in your trial`;
    } else if (hoursRemaining > 1) {
      return `${hoursRemaining} hours left in your trial`;
    } else if (hoursRemaining === 1) {
      return `1 hour left in your trial`;
    } else {
      return `Less than 1 hour left in your trial`;
    }
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 py-2 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <span className="text-lg">✨</span>
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              <span className="font-medium text-blue-700">{getTimeMessage()}</span>
              {' '} • Enjoying Pro features? Upgrade now to keep them forever.
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/payment')}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-full hover:bg-blue-700 transition-colors font-medium"
          >
            Upgrade Now
          </button>
          <button
            onClick={() => setVisible(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors text-lg leading-none"
            title="Hide banner"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
